import {
    Accordion,
    AccordionContent,
    AccordionPanel,
    AccordionTitle,
    Badge,
    Button,
    Card,
    Progress
} from 'flowbite-react';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { HiArrowRightStartOnRectangle } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QueryResult } from '../../components/Alerts.tsx';
import { useBuildingContext } from '../../components/BuildingContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import NotificationBadge from '../../components/NotificationBadge.tsx';
import RequiresBuilding from '../../components/RequiresBuilding.tsx';
import { toastError } from '../../components/Toast.tsx';
import UserAvatar, { UserLike } from '../../components/UserAvatar.tsx';
import { useAppSelector } from '../../lib/hooks';
import {
    CompanyKantoordagView,
    KantoordagView,
    useAfmeldenMutation,
} from '../../lib/model/kantoordagApi.ts';
import { useGetKantoordagDayViewQuery } from '../../lib/model/kantoordag/kantoordagDayViewStreamingApi.ts';
import { DateTime } from 'luxon';
import { userHasRole } from '../../lib/user-has-role.tsx';

export default function DayOverview({day, showEmpty} : {day: DateTime, showEmpty: boolean}) {
    return (
        <RequiresBuilding>
            <DayOverviewInternal day={day} showEmpty={showEmpty} />
        </RequiresBuilding>
    )
}

function DayOverviewInternal({day, showEmpty} : {day: DateTime, showEmpty: boolean}) {
    const navigate = useNavigate();
    const selectedBuilding = useBuildingContext();
    const user = useAppSelector(state => state.user.user)
    const {t} = useTranslation("overview");
    const [signOff, result] = useAfmeldenMutation()
    const [showConfirmSignoff, setShowConfirmSignoff] = useState(false);
    const [userToSignOff, setUserToSignOff] = useState<string>("");

    const query = useGetKantoordagDayViewQuery({
        date: day.toISODate()!,
        building: selectedBuilding.id,
    })

    const isSignedUp = query.isSuccess &&
        Object.values(query.data.companies)
            .flatMap(company => company.users)
            .map(userView => userView.userId)
            .indexOf(user?.email ?? "") != -1

    function doSignOffUser(user: string) {
        signOff({
            datum: day.toISODate()!,
            userId: user
        }).unwrap().then(() => {
            toast.success(t("signoff_success"))
            // No refetch needed — Firestore onSnapshot propagates the update automatically
        }).catch(() => {
            toastError({api: result, message: t("signoff_failed")})
        })
    }

    function doSignOff() {
        doSignOffUser(user!.email)
    }

    function adminSignOff(user: string) {
        doSignOffUser(user)
    }

    function signUp() {
        navigate("/signup/" + day.toISODate())
    }

    function noClicked() {
        setShowConfirmSignoff(false)
    }

    function yesClicked() {
        setShowConfirmSignoff(false)
        adminSignOff(userToSignOff)
    }

    const onDelete = userHasRole(user, ["ADMIN"]) ? (user: UserLike) => {
        setShowConfirmSignoff(true)
        setUserToSignOff(user.userId)
    } : undefined

    /**
     * We don't use CompanyOverview and EmptyOverview as TSX elements, as <Accordion> can't handle a child element
     * in between.
     */
    const companiesToShow = Object.values(query.data?.companies ?? [])
        .filter(company => showEmpty || company.users.length > 0)
        .map(company => CompanyOverview(company, t, onDelete))
    const noSignUps = companiesToShow.length == 0
    const companyElements =  (noSignUps) ? (EmptyOverview(t)) : companiesToShow

    return (
        <Card className={"w-full h-full"}>
            <h5>{day.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}</h5>

            <QueryResult query={query} message={t("loading_failed")}>
                {
                    query.data !== undefined ?
                        (
                            <>
                                <KantoordagOverview overview={query.data!.summary}/>
                                {
                                    isSignedUp ?
                                        <Button color={"primary"} onClick={doSignOff}>{t("signOff")}</Button>
                                        :
                                        <Button color={"secondary"} onClick={signUp}>{t("signUp")}</Button>
                                }
                                <Accordion>
                                    {
                                        companyElements
                                    }
                                </Accordion>
                            </>
                        )
                        : null
                }
            </QueryResult>

            <ConfirmDialog
                message={t('sure_to_signoff')}
                yes={t('yes')}
                no={t('no')}
                show={showConfirmSignoff}
                onYes={yesClicked}
                onNo={noClicked}
            />
        </Card>
    )
}

function KantoordagOverview({overview}: {overview: KantoordagView}) {
    return (
        <div className={"relative"}>
            <NotificationBadge>{overview.assignedSpots}</NotificationBadge>
            <Progress progress={overview.capacityPercentage ?? 0}
                      labelText
                      textLabelPosition={"outside"}
                      textLabel={overview.spotReservations + " / " + overview.capacity}/>

            {(overview.waitlistSize ?? 0) > 0 ?
                <Badge color={"failure"}>
                    Waitlist: { overview.waitlistSize }
                </Badge>
                : null
            }
        </div>
    )
}

function EmptyOverview(t: TFunction<string, undefined>) {
    return (
        <AccordionPanel>
            <AccordionTitle className={"relative italic"}>
                <Trans t={t}>no_reservations</Trans>
            </AccordionTitle>
            <AccordionContent>
            </AccordionContent>
        </AccordionPanel>
    )
}

function CompanyOverview(company: CompanyKantoordagView, t: TFunction<string, undefined>, onDelete?: (user: UserLike) => void) {
    return (
        <AccordionPanel key={company.companyName}>
            <AccordionTitle className={"relative"}>
                <NotificationBadge>{company.totalReservations}</NotificationBadge>

                {
                    company.companyLogo.length == 0 ?
                        <span>
                            <HiArrowRightStartOnRectangle/>
                            <Trans t={t}>external</Trans>
                        </span>
                        :
                        <img src={company.companyLogo} alt={company.companyName} className={"h-6"}/>
                }


            </AccordionTitle>
            <AccordionContent>
                <div className={"flex flex-row flex-wrap"}>
                    {
                        company.users.map( user =>
                            <UserAvatar key={user.userId} user={user} onDelete={onDelete}/>
                        )
                    }
                </div>
            </AccordionContent>
        </AccordionPanel>
    )
}